import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

import {
  formatTradePickerLabel,
  type TradePickerRow,
  useTradePicker,
} from "../../api/hooks/useTradePicker";
import { Input } from "../ui/input";

const SEARCH_DEBOUNCE_MS = 300;

export interface TradePickerProps {
  value: string;
  onChange: (tradeId: string) => void;
  mode?: "create" | "edit";
  disabled?: boolean;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

// Searchable trade picker (U4c): a combobox over the user's trades, listing each
// as "SYMBOL · date · P&L". Mirrors InstrumentTypeahead's interaction model
// (debounced query, keyboard nav, click-outside) but is purely presentational --
// all data concerns live in useTradePicker.
export function TradePicker({
  value,
  onChange,
  mode = "create",
  disabled,
  id,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: TradePickerProps) {
  const listboxId = useId();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  // The row the user just picked, so the closed field can render its label even
  // when the trade falls outside the currently-loaded rows (older recent).
  const [picked, setPicked] = useState<TradePickerRow | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { rows, isLoading, isError, truncated, selectedLabel, selectedState, recordRecent } =
    useTradePicker({ value, mode, debouncedQuery });

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [query]);

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("mousedown", onClick);
    };
  }, []);

  useEffect(() => {
    if (highlight >= rows.length) setHighlight(0);
  }, [rows, highlight]);

  // Resolve the label shown in the closed field for the current selection.
  const selectedDisplay =
    picked !== null && picked.trade_id === value
      ? formatTradePickerLabel(picked)
      : value !== ""
        ? (selectedLabel ??
          (selectedState === "deleted"
            ? "Trade no longer available"
            : selectedState === "loading"
              ? "Loading…"
              : ""))
        : "";
  const displayValue = isOpen ? query : selectedDisplay;

  function selectRow(row: TradePickerRow): void {
    onChange(row.trade_id);
    recordRecent(row.trade_id);
    setPicked(row);
    setQuery("");
    setIsOpen(false);
    inputRef.current?.blur();
  }

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!isOpen) setIsOpen(true);
      setHighlight((h) => (rows.length === 0 ? 0 : (h + 1) % rows.length));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) setIsOpen(true);
      setHighlight((h) => (rows.length === 0 ? 0 : (h - 1 + rows.length) % rows.length));
    } else if (event.key === "Enter") {
      const row = rows[highlight];
      if (isOpen && row !== undefined) {
        event.preventDefault();
        selectRow(row);
      }
    } else if (event.key === "Escape") {
      if (isOpen) {
        event.preventDefault();
        setIsOpen(false);
      }
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        ref={inputRef}
        id={id}
        data-testid="trade-picker-input"
        role="combobox"
        type="text"
        autoComplete="off"
        spellCheck={false}
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        disabled={disabled}
        placeholder="Search trades by symbol…"
        value={displayValue}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
          setHighlight(0);
        }}
        onFocus={() => {
          setIsOpen(true);
        }}
        onKeyDown={onInputKeyDown}
      />
      {isOpen && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-md border border-outline bg-surface-container shadow-lg"
        >
          {isLoading ? (
            <li className="px-3 py-2 text-sm text-on-surface-muted">Loading trades…</li>
          ) : isError ? (
            <li role="alert" className="px-3 py-2 text-sm text-danger">
              Failed to load trades.
            </li>
          ) : rows.length === 0 ? (
            <li className="px-3 py-2 text-sm text-on-surface-muted">
              {debouncedQuery.trim() === "" ? "No trades yet." : "No trades match."}
            </li>
          ) : (
            rows.map((row, index) => {
              const isHighlighted = index === highlight;
              return (
                <li
                  key={row.trade_id}
                  role="option"
                  aria-selected={isHighlighted}
                  data-testid={`trade-picker-option-${row.trade_id}`}
                  className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm ${
                    isHighlighted ? "bg-surface-container-high" : "hover:bg-surface-container-high"
                  }`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectRow(row);
                  }}
                  onMouseEnter={() => {
                    setHighlight(index);
                  }}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-medium text-on-surface">
                      {row.symbol === "" ? "—" : row.symbol}
                    </span>
                    <span className="text-xs text-on-surface-muted">
                      {row.openedDate} · {row.pnl}
                    </span>
                  </span>
                  {row.kind === "recent" && (
                    <span className="text-[10px] uppercase tracking-wide text-on-surface-muted">
                      Recent
                    </span>
                  )}
                </li>
              );
            })
          )}
          {truncated && (
            <li className="border-t border-outline-variant px-3 py-2 text-xs text-on-surface-muted">
              Showing the first {rows.length}. Refine your search to narrow results.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
